import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { Box, TextField, Typography, IconButton, Button } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import AlbumLayout from "./AlbumLayout";
import AdBlockComponent from "../Other/AdBlockComponent";
import PictureCard from "../Other/PictureCard";
import cloneDeep from "lodash/cloneDeep";
import TierNameBlock from "../Other/TierNameBlock";
import TierListOptions from "../Other/TierListOptions";
import { DEFAULT_TIER_COLORS, FALLBACK_TIER_COLOR, TIER_COLORS } from "../../Styles/theme";

const getTierColor = (name: string, index: number): string =>
  TIER_COLORS[name] || DEFAULT_TIER_COLORS[index % DEFAULT_TIER_COLORS.length] || FALLBACK_TIER_COLOR;

const TierListLayout = (props) => {
  const theme = props.basicFunctions.getTheme();
  const { albumList, artistList, songList, initialTiers, listType, tierListName, initialTierColors, albumPagination, songPagination, customItems } = props.state;

  // The user's own custom-added items live alongside the site catalogue in each pool.
  const customAlbums = useMemo(() => customItems?.Albums || [], [customItems]);
  const customArtists = useMemo(() => customItems?.Artists || [], [customItems]);
  const customSongs = useMemo(() => customItems?.Songs || [], [customItems]);

  // Build initial color map from URL-provided custom tiers
  const buildInitialColors = (): Record<string, string> => {
    if (!initialTierColors || !Array.isArray(initialTierColors)) return {};
    const colors: Record<string, string> = {};
    initialTierColors.forEach((t: any) => {
      if (t.name && t.color) colors[t.name] = t.color;
    });
    return colors;
  };

  const [artists, setArtists] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [songs, setSongs] = useState([]);
  const [tiers, setTiers] = useState(cloneDeep(initialTiers));
  const tiersRef = useRef(tiers);
  tiersRef.current = tiers;
  const [tierNames, setTierNames] = useState(Object.keys(cloneDeep(initialTiers)));
  const [tierColors, setTierColors] = useState<Record<string, string>>(buildInitialColors);
  const [editingTier, setEditingTier] = useState<{ index: number; name: string; color: string } | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [haveNames, setHaveNames] = useState(true);
  const [editingTitle, setEditingTitle] = useState(false);
  const { t } = useTranslation();

  const getEffectiveColor = (tier: string, index: number) =>
    tierColors[tier] || getTierColor(tier, index);

  // Sync custom tier config to parent state for use during screenshot capture
  useEffect(() => {
    const customTiers = tierNames.map((name, i) => ({
      name,
      color: getEffectiveColor(name, i),
    }));
    props.basicFunctions.updateState({ customTiers });
  }, [tierNames, tierColors]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync tier data to parent state for use during capture
  useEffect(() => {
    const tierData = {
      tierNames,
      tiers: Object.fromEntries(
        tierNames.map(name => [
          name,
          (tiers[name] || []).map(item => ({
            Id: item.Id,
            ...(item.AlbumName ? { AlbumName: item.AlbumName } : {}),
            ...(item.SongName ? { SongName: item.SongName } : {}),
            ...(item.Artist ? { Artist: item.Artist } : {}),
            ...(item.ArtistId ? { ArtistId: item.ArtistId } : {}),
            ...(item.YearReleased ? { YearReleased: item.YearReleased } : {}),
            ...(item.AlbumId ? { AlbumId: item.AlbumId } : {}),
            ...(item.ProfilePicture && !item.IsCustom ? { ProfilePicture: item.ProfilePicture } : {}),
            ...(item.IsCustom ? { IsCustom: true, CustomImage: item.CustomImage } : {}),
          }))
        ])
      ),
    };
    props.basicFunctions.updateState({ tierData });
  }, [tierNames, tiers]); // eslint-disable-line react-hooks/exhaustive-deps

  const moveTier = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= tierNames.length) return;
    const newNames = [...tierNames];
    [newNames[index], newNames[target]] = [newNames[target], newNames[index]];
    setTierNames(newNames);
  };

  const handleEditTier = (index: number) => {
    const tier = tierNames[index];
    setEditingTier({ index, name: tier, color: getEffectiveColor(tier, index) });
  };

  const handleSaveEdit = () => {
    if (!editingTier) return;
    const { index, name, color } = editingTier;
    const oldName = tierNames[index];

    // Update color
    setTierColors((prev) => {
      const updated = { ...prev };
      if (oldName !== name) delete updated[oldName];
      updated[name] = color;
      return updated;
    });

    // Update name if changed
    if (oldName !== name) {
      const newNames = [...tierNames];
      newNames[index] = name;
      setTierNames(newNames);

      const updatedTiers = { ...tiers };
      updatedTiers[name] = updatedTiers[oldName];
      delete updatedTiers[oldName];
      Object.keys(updatedTiers).forEach((tier) => {
        updatedTiers[tier] = updatedTiers[tier].map((item) =>
          item.Tier === oldName ? { ...item, Tier: name } : item
        );
      });
      setTiers(updatedTiers);
    }

    setEditingTier(null);
  };

  const handleAddTier = () => {
    const alphabet = "SABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
    const usedNames = new Set(tierNames);
    let newName = "New";
    for (const letter of alphabet) {
      if (!usedNames.has(letter)) {
        newName = letter;
        break;
      }
    }
    const newColor = DEFAULT_TIER_COLORS[tierNames.length % DEFAULT_TIER_COLORS.length];
    setTierNames([...tierNames, newName]);
    setTiers({ ...tiers, [newName]: [] });
    setTierColors((prev) => ({ ...prev, [newName]: newColor }));
  };

  const handleDeleteTier = (index: number) => {
    if (tierNames.length <= 1) return;
    const tierToDelete = tierNames[index];
    const itemsToReturn = tiers[tierToDelete] || [];

    // Return items to the appropriate pool
    if (itemsToReturn.length > 0) {
      if (listType === "Artists") {
        setArtists((prev) => [...prev, ...itemsToReturn]);
      } else if (listType === "Songs") {
        setSongs((prev) => [...prev, ...itemsToReturn]);
      } else {
        setAlbums((prev) => [...prev, ...itemsToReturn]);
      }
    }

    // Remove tier
    const newNames = tierNames.filter((_, i) => i !== index);
    setTierNames(newNames);
    const updatedTiers = { ...tiers };
    delete updatedTiers[tierToDelete];
    setTiers(updatedTiers);
    setTierColors((prev) => {
      const updated = { ...prev };
      delete updated[tierToDelete];
      return updated;
    });
    setEditingTier(null);
  };

  const handleArtistFilterChange = useCallback(async (artist: string | null) => {
    if (listType === "Songs") {
      await props.mainFunctions.filterSongsByArtist(artist);
    } else {
      await props.mainFunctions.filterAlbumsByArtist(artist);
    }
  }, [listType, props.mainFunctions]);

  const handleYearChange = useCallback(async (year: string) => {
    await props.mainFunctions.filterAlbumsByYear(year);
  }, [props.mainFunctions]);

  const handleLoadMore = useCallback(async () => {
    const currentArtistFilter = props.state.initialGroupByValue;
    const artistArg = currentArtistFilter && currentArtistFilter !== "All" ? currentArtistFilter : undefined;
    if (listType === "Artists") {
      await props.mainFunctions.loadMoreArtists();
    } else if (listType === "Songs") {
      await props.mainFunctions.loadMoreSongs(artistArg);
    } else {
      await props.mainFunctions.loadMoreAlbums(artistArg);
    }
  }, [listType, props.mainFunctions, props.state.initialGroupByValue]);

  const currentPagination = listType === "Songs" ? songPagination : listType === "Artists" ? props.state.artistPagination : albumPagination;

  const covers = {
    albumCovers: {},
    profilePictures: {},
  };
  let coverType = "albumCovers";

  if (listType === "Albums" || listType === "Year") {
    coverType = "albumCovers";
    // eslint-disable-next-line react-hooks/rules-of-hooks
    covers.albumCovers = useMemo(() => {
      const covers = {};
      if (albumList && Array.isArray(albumList)) {
        albumList.forEach((album) => {
          if (!covers[album.Id]) {
            covers[album.Id] = props.basicFunctions.getAlbumArt(album.Id);
          }
        });
      }

      return covers;
    }, [albumList, props.basicFunctions]);
  } else if (listType === "Artists") {
    coverType = "profilePictures";
    // eslint-disable-next-line react-hooks/rules-of-hooks
    covers.profilePictures = useMemo(() => {
      const covers = {};
      artistList.forEach((artist) => {
        if (!covers[artist.Id]) {
          covers[artist.Id] = props.mainFunctions.getProfilePicture(
            artist.Artist,
            artist.ProfilePicture,
            artist.ProfilePicture
          );
        }
      });
      return covers;
    }, [artistList, props.mainFunctions]);
  } else if (listType === "Songs") {
    coverType = "albumCovers";
    // eslint-disable-next-line react-hooks/rules-of-hooks
    covers.albumCovers = useMemo(() => {
      const covers = {};
      if (songList && Array.isArray(songList)) {
        songList.forEach((song) => {
          if (song.AlbumId && !covers[song.Id]) {
            covers[song.Id] = props.basicFunctions.getAlbumArt(song.AlbumId);
          }
        });
      }
      return covers;
    }, [songList, props.basicFunctions]);
  }

  useEffect(() => {
    if (albumList.length > 0 || customAlbums.length > 0) {
      const tierItemIds = new Set(Object.values(tiersRef.current).flat().map((item: any) => item.Id));
      setAlbums(cloneDeep([...customAlbums, ...albumList].filter((item) => !tierItemIds.has(item.Id))));
    }
  }, [albumList, customAlbums]);

  useEffect(() => {
    if (artistList.length > 0 || customArtists.length > 0) {
      const tierItemIds = new Set(Object.values(tiersRef.current).flat().map((item: any) => item.Id));
      setArtists(cloneDeep([...customArtists, ...artistList].filter((item) => !tierItemIds.has(item.Id))));
    }
  }, [artistList, customArtists]);

  useEffect(() => {
    if ((songList && songList.length > 0) || customSongs.length > 0) {
      const tierItemIds = new Set(Object.values(tiersRef.current).flat().map((item: any) => item.Id));
      setSongs(cloneDeep([...customSongs, ...(songList || [])].filter((item) => !tierItemIds.has(item.Id))));
    }
  }, [songList, customSongs]);

  const renderedTierItems = useMemo(
    () => (listType === "Albums" || listType === "Songs" ? Object.values(tiers).flat() : []),
    [listType, tiers]
  );

  return (
    <ThemeProvider theme={theme}>
      <Box
        id="themeProviderBox"
        sx={{
          display: "flex",
          flexDirection: "column",
          overflow: "hidden scroll",
          height: "-webkit-fill-available",
        }}
      >
        <Box
          id="tierBox"
          sx={{
            padding: 3,
            display: "-webkit-box",
            gap: 4,
            width: "100%",
          }}
        >
          <DragDropContext
            onDragEnd={(result) => {
              const dragProps = {
                result,
                setAlbums,
                setArtists,
                setSongs,
                listType,
                artists,
                songs,
                setTiers,
                albums,
                tiers,
              };
              props.tierListFunctions.onDragEnd(dragProps);
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1,
                  overflowY: "auto",
                }}
                id="tierList"
              >
                <Box id="tierListName" sx={{ textAlign: "center", mb: 0.5 }}>
                  {editingTitle ? (
                    <TextField
                      value={tierListName}
                      onChange={(e) => props.basicFunctions.updateState({ tierListName: e.target.value })}
                      onBlur={() => setEditingTitle(false)}
                      onKeyDown={(e) => { if (e.key === "Enter") setEditingTitle(false); }}
                      autoFocus
                      variant="standard"
                      placeholder={t("tierList.titlePlaceholder")}
                      slotProps={{ input: { style: { textAlign: "center", fontSize: "1.5rem", fontWeight: "bold" } } }}
                      sx={{ width: "60%" }}
                    />
                  ) : (
                    <Box
                      onClick={() => setEditingTitle(true)}
                      sx={{
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1,
                        "&:hover .edit-icon": { opacity: 1 },
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", minHeight: "1.5rem" }}
                      >
                        {tierListName || (
                          <Typography component="span" sx={{ color: "text.disabled", fontWeight: "bold", fontSize: "1.5rem" }}>
                            {t("tierList.titlePlaceholder")}
                          </Typography>
                        )}
                      </Typography>
                      <IconButton size="small" className="edit-icon" sx={{ opacity: 0, transition: "opacity 0.15s" }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>

                {tierNames.map((tier, index) => {
                  const tierColor = getEffectiveColor(tier, index);
                  return (
                  <Droppable
                    key={tier}
                    droppableId={tier}
                    ignoreContainerClipping={false}
                    isDropDisabled={false}
                    isCombineEnabled={false}
                  >
                    {(provided) => {
                      return (
                        <Box
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          p={2}
                          position="relative"
                          display="flex"
                          gap="20px"
                          sx={{
                            bgcolor: "var(--surface-1)",
                            borderRadius: "8px",
                            border: "1px solid var(--border-soft)",
                            borderLeft: `5px solid ${tierColor}`,
                            marginBottom: "4px",
                          }}
                        >
                          <TierNameBlock
                            index={index}
                            tier={tier}
                            tierColor={tierColor}
                            hoveredIndex={hoveredIndex}
                            setHoveredIndex={setHoveredIndex}
                            onEdit={() => handleEditTier(index)}
                            onMoveUp={() => moveTier(index, -1)}
                            onMoveDown={() => moveTier(index, 1)}
                            isFirst={index === 0}
                            isLast={index === tierNames.length - 1}
                            editingTier={editingTier}
                            setEditingTier={setEditingTier}
                            onSaveEdit={handleSaveEdit}
                            onDeleteTier={() => handleDeleteTier(index)}
                            canDelete={tierNames.length > 1}
                          />
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
                              gap: 1,
                              width: "100%",
                            }}
                          >
                            {tiers[tier] &&
                              tiers[tier].map((item, index) => {
                                const coverItem = covers[coverType][item.Id];
                                return (
                                  <PictureCard
                                    key={item.Id}
                                    size={haveNames ? 150 : 100}
                                    imageSize={100}
                                    haveNames={haveNames}
                                    keyItem={item.Id}
                                    pictureItem={item}
                                    cover={coverItem}
                                    index={index}
                                    draggableId={item.Id.toString()}
                                    provided={provided}
                                  />
                                );
                              })}
                          </Box>
                          {provided.placeholder}
                        </Box>
                      );
                    }}
                  </Droppable>
                  );
                })}
                <Button
                  onClick={handleAddTier}
                  startIcon={<AddIcon />}
                  variant="outlined"
                  size="small"
                  id="addTierButton"
                  sx={{
                    mt: 0.5,
                    alignSelf: "flex-start",
                    textTransform: "none",
                    borderColor: "var(--border-mid)",
                    color: "var(--text-secondary)",
                    "&:hover": {
                      borderColor: "var(--border-strong)",
                      backgroundColor: "var(--hover-overlay)",
                    },
                  }}
                >
                  {t("tierList.addTier")}
                </Button>
              </Box>
              <AdBlockComponent {...props} adSlot="3525069703" />
            </Box>
            {(albumList.length > 0 || artistList.length > 0 || (songList && songList.length > 0)) && (
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <TierListOptions
                  {...props}
                  setHaveNames={setHaveNames}
                  haveNames={haveNames}
                  setArtists={setArtists}
                  setSongs={setSongs}
                  setTiers={setTiers}
                  setAlbums={setAlbums}
                  tierListTitle={tierListName}
                />
                <Droppable
                  droppableId="albumList"
                  ignoreContainerClipping={false}
                  isDropDisabled={false}
                  isCombineEnabled={false}
                >
                  {(provided) => (
                    <AlbumLayout
                      {...props}
                      provided={provided}
                      albums={albums}
                      artists={artists}
                      songs={songs}
                      coverType={coverType}
                      covers={covers}
                      renderedItems={renderedTierItems}
                      updateUrlParams={props.updateUrlParams}
                      onArtistFilterChange={handleArtistFilterChange}
                      onYearChange={handleYearChange}
                      onLoadMore={handleLoadMore}
                      onShuffle={() => props.mainFunctions.shuffleAlbums()}
                      pagination={currentPagination}
                    />
                  )}
                </Droppable>
              </Box>
            )}
          </DragDropContext>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default TierListLayout;
